import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_sydney-distributor/artifacts/l39oq0om_Untitled%20%28Your%20Story%29%20%281600%20x%20680%20px%29.png";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Why Us', action: () => scrollToSection('why-us') },
    { label: 'Products', action: () => scrollToSection('products') },
    { label: 'Who We Serve', action: () => scrollToSection('who-we-serve') },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#01195d] shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="GS Pacific"
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link, index) => (
              link.href ? (
                <Link
                  key={index}
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-sm font-medium text-white/90 hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={index}
                  onClick={link.action}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-sm font-medium text-white/90 hover:text-white transition-colors"
                >
                  {link.label}
                </button>
              )
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:1800934114"
              data-testid="nav-phone"
              className="flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              1800 934 114
            </a>
            <Link
              to="/trade-application"
              data-testid="nav-trade-application"
              className="border border-white/30 text-white hover:bg-white/10 h-10 px-5 rounded-md font-medium text-sm flex items-center transition-colors"
            >
              Trade Application
            </Link>
            <Link
              to="/order"
              data-testid="nav-order-online"
              className="bg-green-600 text-white hover:bg-green-700 h-10 px-5 rounded-md font-medium text-sm flex items-center transition-colors"
            >
              Order Online
            </Link>
            <Button
              data-testid="nav-cta-button"
              onClick={() => scrollToSection('enquiry-form')}
              className="bg-white text-[#01195d] hover:bg-white/90 h-10 px-5 rounded-md font-medium"
            >
              Become a Partner
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-white"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white border-t border-slate-100 shadow-lg"
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link, index) => (
              link.href ? (
                <Link
                  key={index}
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block text-slate-700 font-medium py-2"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={index}
                  onClick={link.action}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block text-slate-700 font-medium py-2 w-full text-left"
                >
                  {link.label}
                </button>
              )
            ))}
            <Link
              to="/trade-application"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-slate-700 font-medium py-2"
            >
              Trade Application
            </Link>
            <Link
              to="/order"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-green-600 font-medium py-2"
            >
              Order Online
            </Link>
            <a
              href="tel:1800934114"
              className="flex items-center gap-2 text-[#01195d] font-medium py-2"
            >
              <Phone className="w-4 h-4" />
              1800 934 114
            </a>
            <Button
              onClick={() => scrollToSection('enquiry-form')}
              className="w-full bg-[#01195d] hover:bg-[#01195d]/90 text-white h-12 rounded-md font-medium"
            >
              Become a Partner
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
